import Path from "../Path";
import PathPlan from "../plan/PathPlan";
import { Template } from "./Template";

/**
 * @abstract
 */
export default class PathTemplatePattern {
    /**
     * @param {Array.<String>} stringPattern 
     * @param {boolean} horizontalFlip
     */
    constructor(stringPattern, horizontalFlip) {
        /**
         * @type {number}
         */
        this.startCol = 0;
        /**
         * @type {number}
         */
        this.startRow = 0;
        /**
         * @type {number}
         */
        this.endCol = 0;
        /**
         * @type {number}
         */
        this.endRow = 0;

        /**
         * @type {number}
         */
        this.startColFlipped = 0;
        /**
         * @type {number}
         */
        this.startRowFlipped = 0;
        /**
         * @type {number}
         */
        this.endColFlipped = 0;
        /**
         * @type {number}
         */
        this.endRowFlipped = 0;

        /**
         * The number of path tiles in the pattern, not counting start or end.
         * This is figured out by the convertPattern() method.
         * @type {number}
         */
        this.pathCount = 0;

        /**
         * @type {Array.<Array.<number>>}
         */
        this.pattern = this.convertPattern(stringPattern); // Side effect of setting start col/row, end col/row

        /**
         * @type {Array.<Array.<number>>}
         */
        this.flippedPattern = horizontalFlip ? this.convertFlippedPattern(stringPattern) : null;

    }

    /**
     * Returns the minimum number of path steps required by this pattern to be applied.
     * @abstract
     * @returns {number}
     */
    getMinRequiredPathSteps() {
    }

    /**
     * @param {Path} path
     * @param {number} pathIndex The path index.
     * @param {number} templateCol The col index in the template of the current path step
     * @param {number} templateRow The row index in the template of the current path step
     * @param {Array.<Array.<number>>} template The template grid.
     * @returns {PathPlan}
     */
    getPlanIfMatch(path, pathIndex, templateCol, templateRow, template) {
        if (this.isMatch(templateCol, templateRow, template, this.pattern, this.startCol, this.startRow, this.endCol, this.endRow)) {
            return this.getPathPlan(path, pathIndex);
        }

        if (!this.flippedPattern) {
            return null;
        }

        if (this.isMatch(templateCol, templateRow, template, this.flippedPattern, this.startColFlipped, this.startRowFlipped, this.endColFlipped, this.endRowFlipped)) {
            return this.getPathPlanFlipped(path, pathIndex);
        }

        return null;
    }

    /**
     * @abstract
     * @param {Path} path
     * @param {number} pathIndex The path index.
     * @returns {PathPlan}
     */
    getPathPlan(path, pathIndex) {}

    /**
     * @abstract
     * @param {Path} path
     * @param {number} pathIndex The path index.
     * @returns {PathPlan}
     */
    getPathPlanFlipped(path, pathIndex) {}

    /**
     * @private
     * @param {number} templateCol column index in template that corresponds with current path step col.
     * @param {number} templateRow row index in template that corresponds with current path step row.
     * @param {Array.<Array.<number>>} template The main path template
     * @param {Array.<Array.<number>>} pattern
     * @param {number} patternStartCol
     * @param {number} patternStartRow
     * @param {number} patternEndCol
     * @param {number} patternEndRow
     * @returns {boolean}
     */
    isMatch(templateCol, templateRow, template, pattern, patternStartCol, patternStartRow, patternEndCol, patternEndRow) {
        if (template.length === 0) {
            return false;
        }

        // Find the [col,row] coordinate of the pattern origin in the template.
        const tOriginCol = templateCol - patternStartCol;
        const tOriginRow = templateRow - patternStartRow;
        if (tOriginCol < 0 || tOriginRow < 0) {
            return false;
        }

        ///////////////////////////////////////////////////////////////
        // We know where the start and end tiles are in the pattern.  
        // We can quickly check those tiles to be see if this pattern could possibly work.
        ///////////////////////////////////////////////////////////////

        // Get the template value that corresponds with the start of the pattern.
        const tValAtStart = template[templateCol][templateRow];
        if (tValAtStart < 0) {
            // The start tile isn't a path tile.
            return false;
        }

        const tEndCol = templateCol + (patternEndCol - patternStartCol);
        const tEndRow = templateRow + (patternEndRow - patternStartRow);
        
        // Probably redundant
        if (tEndCol >= template.length) {
            return false;
        }

        const templateEndRow = template[tEndCol];
        if (tEndRow >= templateEndRow.length) {
            return false;
        }
        
        const tValAtEnd = templateEndRow[tEndRow];
        if (tValAtEnd < tValAtStart) {
            // Not only does the end tile need to be positive, it needs to be greater than the start tile.
            return false;
        }

        for (let pCol = 0; pCol < pattern.length; pCol++) {
            const patternRow = pattern[pCol];
            for (let pRow = 0; pRow < patternRow.length; pRow++) {
                const pVal = patternRow[pRow];
                if (pVal === Template.IGNORE || pVal === Template.START || pVal === Template.END) {
                    continue;
                }

                // get the corresponding cell index in the template
                const tCol = tOriginCol + pCol;
                const tRow = tOriginRow + pRow;
                if (tCol >= template.length) {
                    return false;
                }

                const templateRows = template[tCol];
                if (tRow >= templateRows.length) {
                    return false;
                }

                // get the template value
                const tVal = templateRows[tRow];

                if (pVal === Template.OPEN_OR_PATH) {
                    if (tVal === Template.OPEN || tVal > 0) {  // tVal greater than 0 is path
                        continue;
                    }
                    else {
                        return false;
                    }
                }
                else if (pVal === Template.OPEN) {
                    if (tVal === Template.OPEN) {
                        continue;
                    }
                    else {
                        return false;
                    }
                }
                else if (pVal === Template.CLOSED) {
                    if (tVal === Template.CLOSED) {
                        continue;
                    }
                    else {
                        return false;
                    }
                }                
                else if (pVal === Template.PATH) {
                    if (tVal < 0) {
                        return false; // both template and pattern must be path tiles
                    }
                }
                else {
                    return false;
                }
            }
        }

        return true;
    }


// String Pattern:
// S start
// P path
// E end
// X closed
// O open
// . ignore

    /**
     * @private
     * @param {Array.<string>} stringPattern 
     * @returns {Array.<Array.<number>>}
     */
    convertPattern(stringPattern) {
        if (!stringPattern || stringPattern.length === 0) {
            console.log('PathTemplatePattern.convertPattern() Invalid empty string pattern');
            return [];
        }

        const cols = stringPattern[0].length;
        const rows = stringPattern.length;

        // Create the pattern array
        const pattern = new Array(cols);
        for (let col = 0; col < cols; col++) {
            const rowArray = new Array(rows);
            rowArray.fill(Template.IGNORE);
            pattern[col] = rowArray;
        }

        // Fill the array by converting the characters
        for (let row = 0; row < rows; row++) {
            const patternRow = stringPattern[row];
            for (let col = 0; col < cols; col++) {
                const patternChar = patternRow.charAt(col);
                const value = this.convertPatternChar(patternChar);

                if (value === Template.START) {
                    this.startCol = col;
                    this.startRow = row;
                }
                else if (value === Template.END) {
                    this.endCol = col;
                    this.endRow = row;
                }
                else if (value === Template.PATH) {
                    this.pathCount++; // Count up all path tiles that are not start or end.
                }

                pattern[col][row] = value;
            }
        }

        return pattern;
    }

    /**
     * @private
     * @param {Array.<string>} stringPattern 
     * @returns {Array.<Array.<number>>}
     */
    convertFlippedPattern(stringPattern) {
        if (!stringPattern || stringPattern.length === 0) {
            console.log('PathTemplatePattern.convertPattern() Invalid empty string pattern');
            return [];
        }

        const cols = stringPattern[0].length;
        const rows = stringPattern.length;

        // Create the pattern array
        const pattern = new Array(cols);
        for (let col = 0; col < cols; col++) {
            const rowArray = new Array(rows);
            rowArray.fill(Template.IGNORE);
            pattern[col] = rowArray;
        }

        // Fill the array by converting the characters
        for (let row = 0; row < rows; row++) {
            const patternRow = stringPattern[row];

            let cc = 0;
            for (let col = cols - 1; col >= 0; col--) {      // <-- go through the string backwards
                const patternChar = patternRow.charAt(col);
                const value = this.convertPatternChar(patternChar);

                if (value === Template.START) {
                    this.startColFlipped = cc;
                    this.startRowFlipped = row;
                }
                else if (value === Template.END) {
                    this.endColFlipped = cc;
                    this.endRowFlipped = row;
                }

                pattern[cc][row] = value;
                cc++;
            }
        }

        return pattern;
    }


    /**
     * @private
     * @param {string} char 
     * @returns {number}
     */
    convertPatternChar(char) {
        switch (char) {
            case 'S':
                return Template.START;
            case 'E':
                return Template.END;
            case 'P':
                return Template.PATH
            case 'X':
                return Template.CLOSED;
            case 'O':
                return Template.OPEN;
            case '8':
                return Template.OPEN_OR_PATH;
            case '.':
                return Template.IGNORE
        }

        console.log('PathTemplatePattern.convertPatternChar() Invalid char: ' + char);
        return Template.IGNORE;
    }

    /**
     * @param {Path} path
     * @param {number} startIndex 
     * @param {number} tileCol 
     * @param {number} tileRow 
     * @returns {number}
     */
    findTilePathIndex(path, startIndex, tileCol, tileRow) {
        for (let i = startIndex; i < path.size; i++) {
            const tile = path.tiles[i];
            const col = tile.getTileCol();
            if (col !== tileCol) {
                continue;
            }

            const row = tile.getTileRow();
            if (row != tileRow) {
                continue;
            }

            return i;
        }

        console.log('PathTemplatePattern.findTilePathIndex() failed to find tile in path.');
        return -1;
    }

    printPattern(pattern) {
        if (pattern.length === 0) {
            console('printPattern() template empty');
            return;
        }

        const rows = pattern[0].length;
        for (let row = 0; row < rows; row++) {
            let s = '';
            for (let col = 0; col < pattern.length; col++) {
                const v = pattern[col][row];
                if (v === Template.IGNORE) {
                    s += '.';
                }
                else if (v === Template.PATH) {
                    s += 'P';
                }
                else if (v === Template.OPEN) {
                    s += '0';
                }
                else if (v === Template.OPEN_OR_PATH) {
                    s += '8';
                }
                else if (v === Template.CLOSED) {
                    s += 'X';
                }
                else if (v === Template.START) {
                    s += 'S';
                }
                else if (v === Template.END) {
                    s += 'E';
                }
                else {
                    s += '?';
                }
            }
            console.log(s);
        }
    }

    /**
     * @abstract
     * @returns {string}
     */
    toString() {}
}